/**
 * SuperAdminPage — platform-owner view across ALL tenants.
 *
 * Available only to users whose role === "super_admin" of TEN-000001 OR
 * users with a special `platform_admin` flag. The backend already gates
 * /api/tenants to super_admin; this UI provides operational controls.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Users, AlertCircle, PauseCircle, PlayCircle, RefreshCw,
  TrendingUp, Activity, ShieldCheck, Calendar,
} from "lucide-react";
import { RudrikLogo } from "../components/RudrikLogo";

const API = `${window.location.origin}/api`;

export default function SuperAdminPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tenants`, { withCredentials: true });
      setTenants(data.tenants || []);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 403
        ? "Access denied — super-admin role required."
        : (e.response?.data?.detail || "Failed to load tenants"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (tenantId, status) => {
    try {
      await axios.put(`${API}/tenants/${tenantId}/status`, { status },
        { withCredentials: true });
      toast.success(`${tenantId} → ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const filtered = tenants.filter((t) =>
    filter === "all" ? true : t.status === filter
  );

  const stats = {
    total: tenants.length,
    active: tenants.filter((t) => t.status === "active").length,
    suspended: tenants.filter((t) => t.status === "suspended").length,
    trial: tenants.filter((t) => t.plan === "trial").length,
    paid: tenants.filter((t) => t.plan && t.plan !== "trial").length,
  };

  return (
    <div data-testid="super-admin-page" style={{ padding: 24 }}>
      {/* Hero */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ShieldCheck size={22} color="#a855f7" />
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
            Platform Admin
          </h1>
          <span style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7",
            padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 800,
            textTransform: "uppercase", letterSpacing: 0.6 }}>Owner</span>
        </div>
        <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
          Manage all tenants on the Rudrik platform — suspend, activate, monitor usage.
        </p>
      </div>

      {/* Stats */}
      <div data-testid="platform-stats" style={{ display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14, marginBottom: 24 }}>
        <Stat label="Total Tenants" value={stats.total} icon={Building2} color="#22d3ee" />
        <Stat label="Active" value={stats.active} icon={Activity} color="#22c55e" />
        <Stat label="Suspended" value={stats.suspended} icon={PauseCircle} color="#ef4444" />
        <Stat label="On Trial" value={stats.trial} icon={Calendar} color="#f59e0b" />
        <Stat label="Paid" value={stats.paid} icon={TrendingUp} color="#a855f7" />
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { key: "all", label: "All" },
          { key: "active", label: "Active" },
          { key: "suspended", label: "Suspended" },
          { key: "trial_expired", label: "Trial expired" },
        ].map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            style={{
              background: filter === f.key ? "rgba(34,211,238,0.18)" : "#0d1117",
              color: filter === f.key ? "#22d3ee" : "#94a3b8",
              border: `1px solid ${filter === f.key ? "#22d3ee55" : "#1e2530"}`,
              borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 700,
              cursor: "pointer",
            }}>
            {f.label}
          </button>
        ))}
        <button onClick={load} data-testid="reload-tenants"
          style={{ marginLeft: "auto", background: "#0d1117", border: "1px solid #1e2530",
          color: "#94a3b8", padding: "6px 12px", borderRadius: 8, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="super-admin-error" style={{ background: "rgba(239,68,68,0.1)",
          border: "1px solid #ef444466", color: "#fca5a5", padding: 16, borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10 }}>
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Tenant table */}
      {!error && (
        <div data-testid="tenants-table" style={{ background: "#0d1117",
          border: "1px solid #1e2530", borderRadius: 12, overflow: "hidden" }}>
          {loading && <div style={{ padding: 24, color: "#64748b", textAlign: "center" }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 32, color: "#64748b", textAlign: "center" }}>
              No tenants match the filter.
            </div>
          )}
          {!loading && filtered.map((t) => (
            <TenantRow key={t.tenant_id} t={t} onStatus={setStatus} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530",
      borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Icon size={14} color={color} />
        <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b",
          textTransform: "uppercase", letterSpacing: 0.7 }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function TenantRow({ t, onStatus }) {
  const statusColor = {
    active: "#22c55e", suspended: "#ef4444",
    trial_expired: "#f59e0b", deleted: "#475569",
  }[t.status] || "#94a3b8";

  return (
    <div data-testid={`tenant-row-${t.tenant_id}`} style={{
      display: "grid", gridTemplateColumns: "180px 1.5fr 1fr 0.8fr 0.8fr auto",
      alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #1e2530",
      gap: 14,
    }}>
      <div>
        <div style={{ fontFamily: "ui-monospace, monospace", color: "#22d3ee",
          fontSize: 12, fontWeight: 700 }}>{t.tenant_id}</div>
        <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{t.workspace_slug}</div>
      </div>
      <div>
        <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 700 }}>{t.company_name}</div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          {t.industry || "—"} · {t.country || "—"}
        </div>
      </div>
      <div>
        <div style={{ color: "#cbd5e1", fontSize: 12 }}>{t.admin_email}</div>
        <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{t.admin_name}</div>
      </div>
      <div>
        <span style={{ background: t.plan === "trial" ? "rgba(245,158,11,0.15)" : "rgba(168,85,247,0.15)",
          color: t.plan === "trial" ? "#f59e0b" : "#a855f7",
          padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 800,
          textTransform: "uppercase", letterSpacing: 0.6 }}>
          {t.plan || "trial"}
        </span>
        {t.user_count != null && (
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
            <Users size={9} style={{ display: "inline", marginRight: 2 }} />
            {t.user_count} user{t.user_count === 1 ? "" : "s"}
          </div>
        )}
      </div>
      <div>
        <span data-testid={`tenant-status-${t.tenant_id}`} style={{
          background: `${statusColor}22`, color: statusColor,
          padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 800,
          textTransform: "uppercase", letterSpacing: 0.6 }}>
          {t.status}
        </span>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {t.status !== "suspended" && (
          <button onClick={() => onStatus(t.tenant_id, "suspended")}
            data-testid={`suspend-${t.tenant_id}`}
            style={iconBtn("#ef4444")} title="Suspend tenant">
            <PauseCircle size={14} />
          </button>
        )}
        {t.status !== "active" && (
          <button onClick={() => onStatus(t.tenant_id, "active")}
            data-testid={`activate-${t.tenant_id}`}
            style={iconBtn("#22c55e")} title="Activate tenant">
            <PlayCircle size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

const iconBtn = (color) => ({
  background: `${color}15`, border: `1px solid ${color}44`,
  color, borderRadius: 6, padding: "5px 8px", cursor: "pointer",
  display: "inline-flex", alignItems: "center",
});
